// Valuations management page
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createClerkSupabaseClient } from '@/lib/supabase'
import { supabase } from '@/lib/supabase-auth'
import { useGetToken } from '@/lib/hooks/use-auth'
import { useProject } from '@/contexts/project-context'
import { usePermissions } from '@/lib/hooks'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Plus,
  TrendingUp,
  FileText,
  Edit2,
  Trash2
} from 'lucide-react'
import { format } from 'date-fns'
import { Database } from '@/database.types'
import { toast } from 'sonner'

type Valuation = Database['public']['Tables']['valuations']['Row']

export default function ValuationsPage() {
  const getToken = useGetToken()
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [editingValuation, setEditingValuation] = useState<Valuation | null>(null)
  const queryClient = useQueryClient()
  const { currentProject } = useProject()
  const permissions = usePermissions()

  // Form state
  const [valuationDate, setValuationDate] = useState(format(new Date(), 'yyyy-MM-dd'))
  const [valuationType, setValuationType] = useState<string>('409A')
  const [commonSharePrice, setCommonSharePrice] = useState('')
  const [preferredSharePrice, setPreferredSharePrice] = useState('')
  const [postMoneyValuation, setPostMoneyValuation] = useState('')
  const [totalSharesOutstanding, setTotalSharesOutstanding] = useState('')
  const [valuationProvider, setValuationProvider] = useState('')
  const [reportUrl, setReportUrl] = useState('')
  const [notes, setNotes] = useState('')

  const { data: valuations, isLoading } = useQuery<Valuation[]>({
    queryKey: ['valuations', currentProject?.id],
    queryFn: async () => {
      if (!currentProject) return []
      
      // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)
      
      const { data, error } = await authenticatedClient
        .from('valuations')
        .select('*')
        .eq('project_id', currentProject.id)
        .order('valuation_date', { ascending: false })

      if (error) throw error
      return data || []
    },
    enabled: !!currentProject
  })

  const { mutate: saveValuation, isPending: isSaving } = useMutation({
    mutationFn: async (valuationData: Partial<Valuation>) => {
      if (!currentProject) throw new Error('No project selected')

      if (valuationData.id) {
        const { error } = await supabase
          .from('valuations')
          .update(valuationData)
          .eq('id', valuationData.id)

        if (error) throw error
      } else {
        const { error } = await supabase
          .from('valuations')
          .insert({
            project_id: currentProject.id,
            valuation_date: valuationData.valuation_date ?? new Date().toISOString().split('T')[0],
            common_share_price: valuationData.common_share_price ?? 0,
            valuation_type: valuationData.valuation_type ?? '409A',
            preferred_share_price: valuationData.preferred_share_price ?? null,
            post_money_valuation: valuationData.post_money_valuation ?? null,
            total_shares_outstanding: valuationData.total_shares_outstanding ?? null,
            valuation_provider: valuationData.valuation_provider ?? null,
            report_url: valuationData.report_url ?? null,
            notes: valuationData.notes ?? null,
          })

        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['valuations'] })
      resetForm()
      setShowAddDialog(false)
      setEditingValuation(null)
      toast.success('Valuation saved successfully')
    },
    onError: (error: any) => {
      toast.error(`Failed to save valuation: ${error.message}`)
    }
  })

  const { mutate: deleteValuation } = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('valuations')
        .delete()
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['valuations'] })
      toast.success('Valuation deleted successfully')
    },
    onError: (error: any) => {
      toast.error(`Failed to delete valuation: ${error.message}`)
    }
  })

  const handleOpenDialog = (valuation?: Valuation) => {
    if (valuation) {
      setEditingValuation(valuation)
      setValuationDate(format(new Date(valuation.valuation_date), 'yyyy-MM-dd'))
      setValuationType(valuation.valuation_type)
      setCommonSharePrice(valuation.common_share_price.toString())
      setPreferredSharePrice(valuation.preferred_share_price?.toString() || '')
      setPostMoneyValuation(valuation.post_money_valuation?.toString() || '')
      setTotalSharesOutstanding(valuation.total_shares_outstanding?.toString() || '')
      setValuationProvider(valuation.valuation_provider || '')
      setReportUrl(valuation.report_url || '')
      setNotes(valuation.notes || '')
    } else {
      resetForm()
    }
    setShowAddDialog(true)
  }

  const resetForm = () => {
    setValuationDate(format(new Date(), 'yyyy-MM-dd'))
    setValuationType('409A')
    setCommonSharePrice('')
    setPreferredSharePrice('')
    setPostMoneyValuation('')
    setTotalSharesOutstanding('')
    setValuationProvider('')
    setReportUrl('')
    setNotes('')
    setEditingValuation(null)
  }

  const handleSubmit = () => {
    const valuationData: Partial<Valuation> = {
      valuation_date: valuationDate,
      valuation_type: valuationType,
      common_share_price: parseFloat(commonSharePrice),
      preferred_share_price: preferredSharePrice ? parseFloat(preferredSharePrice) : null,
      post_money_valuation: postMoneyValuation ? parseFloat(postMoneyValuation) : null,
      total_shares_outstanding: totalSharesOutstanding ? parseInt(totalSharesOutstanding) : null,
      valuation_provider: valuationProvider || null,
      report_url: reportUrl || null,
      notes: notes || null,
    }

    if (editingValuation) {
      valuationData.id = editingValuation.id
    }

    saveValuation(valuationData)
  }

  const latestValuation = valuations?.[0]

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Valuations</h1>
          <p className="mt-1 text-sm text-gray-600">
            Track 409A valuations and share prices over time
          </p>
        </div>
        {permissions.canEditProject && (
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="h-4 w-4 mr-2" />
            Add Valuation
          </Button>
        )}
      </div>

      {/* Latest Valuation Summary */}
      {latestValuation && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <TrendingUp className="h-5 w-5 mr-2" />
            Latest Valuation
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-500">Common Share Price</p>
              <p className="text-2xl font-semibold text-gray-900">
                ${latestValuation.common_share_price.toFixed(2)}
              </p>
            </div>
            {latestValuation.preferred_share_price && (
              <div>
                <p className="text-sm text-gray-500">Preferred Share Price</p>
                <p className="text-2xl font-semibold text-gray-900">
                  ${latestValuation.preferred_share_price.toFixed(2)}
                </p>
              </div>
            )}
            {latestValuation.post_money_valuation && (
              <div>
                <p className="text-sm text-gray-500">Post-Money Valuation</p>
                <p className="text-2xl font-semibold text-gray-900">
                  ${(latestValuation.post_money_valuation / 1000000).toFixed(1)}M
                </p>
              </div>
            )}
          </div>
          <p className="mt-4 text-sm text-gray-500">
            As of {format(new Date(latestValuation.valuation_date), 'MMMM d, yyyy')}
            {latestValuation.valuation_type === '409A' && ' (409A Valuation)'}
          </p>
        </div>
      )}

      {/* Valuations Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Valuation History</h2>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Common Price
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Preferred Price
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Valuation
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Provider
              </th>
              {permissions.canEditProject && (
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center">
                  <div className="animate-pulse">Loading valuations...</div>
                </td>
              </tr>
            ) : valuations?.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                  No valuations recorded. Add your first valuation to get started.
                </td>
              </tr>
            ) : (
              valuations?.map((valuation) => (
                <tr key={valuation.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {format(new Date(valuation.valuation_date), 'MMM d, yyyy')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      valuation.valuation_type === '409A' 
                        ? 'bg-blue-100 text-blue-800' 
                        : valuation.valuation_type === 'investment_round'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {valuation.valuation_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    ${valuation.common_share_price.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {valuation.preferred_share_price 
                      ? `$${valuation.preferred_share_price.toFixed(2)}`
                      : '-'
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {valuation.post_money_valuation 
                      ? `$${(valuation.post_money_valuation / 1000000).toFixed(1)}M`
                      : '-'
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      {valuation.valuation_provider || '-'}
                      {valuation.report_url && (
                        <a 
                          href={valuation.report_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="ml-2 text-blue-600 hover:text-blue-500"
                        >
                          <FileText className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                  </td>
                  {permissions.canEditProject && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenDialog(valuation)}
                        className="mr-2"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (confirm('Are you sure you want to delete this valuation?')) {
                            deleteValuation(valuation.id)
                          }
                        }}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Valuation Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              {editingValuation ? 'Edit Valuation' : 'Add Valuation'}
            </DialogTitle>
            <DialogDescription>
              Record a new valuation for your company
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="valuationDate">Valuation Date</Label>
                <Input
                  id="valuationDate"
                  type="date"
                  value={valuationDate}
                  onChange={(e) => setValuationDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="valuationType">Valuation Type</Label>
                <Select value={valuationType} onValueChange={setValuationType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="409A">409A Valuation</SelectItem>
                    <SelectItem value="investment_round">Investment Round</SelectItem>
                    <SelectItem value="internal">Internal Estimate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="commonSharePrice">Common Share Price ($)</Label>
                <Input
                  id="commonSharePrice"
                  type="number"
                  step="0.01"
                  value={commonSharePrice}
                  onChange={(e) => setCommonSharePrice(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="preferredSharePrice">Preferred Share Price ($)</Label>
                <Input
                  id="preferredSharePrice"
                  type="number"
                  step="0.01"
                  value={preferredSharePrice}
                  onChange={(e) => setPreferredSharePrice(e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="postMoneyValuation">Post-Money Valuation ($)</Label>
                <Input
                  id="postMoneyValuation"
                  type="number"
                  value={postMoneyValuation}
                  onChange={(e) => setPostMoneyValuation(e.target.value)}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="totalSharesOutstanding">Total Shares Outstanding</Label>
                <Input
                  id="totalSharesOutstanding"
                  type="number"
                  value={totalSharesOutstanding}
                  onChange={(e) => setTotalSharesOutstanding(e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="valuationProvider">Valuation Provider</Label>
              <Input
                id="valuationProvider"
                value={valuationProvider}
                onChange={(e) => setValuationProvider(e.target.value)}
                placeholder="e.g., Carta, Armanino, etc."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="reportUrl">Report URL</Label>
              <Input
                id="reportUrl"
                type="url"
                value={reportUrl}
                onChange={(e) => setReportUrl(e.target.value)}
                placeholder="https://..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional notes about this valuation..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddDialog(false)
                resetForm()
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSaving || !commonSharePrice}>
              {isSaving ? 'Saving...' : 'Save Valuation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}